/**
 * Projections (DESIGN §8): the only path from state to a socket.
 *
 * Every projection builds a fresh object from named fields (a whitelist).
 * Seat views include action affordances generated by the same public
 * validators the reducer uses, so the client holds no game logic.
 */
import type { ActionCommand } from './commands.ts';
import { COMPENDIUM, diag, EXPLORATORY_POLISHING, RISK_IF_WRONG } from './content/compendium.ts';
import { eventCard, type EventCard } from './content/events.ts';
import { objectiveCard } from './content/objectives.ts';
import { PRESETS, shiftHour } from './content/presets.ts';
import { checkSeatCommand, HANDLERS } from './reduce.ts';
import { availableRoutes, freeAssessSource, locumPresent, namedOpinions, supportsNeeded, ventsFree } from './rules/act.ts';
import { BED_OF, clockLength, diagName, hasAccess, listOf, nextStep, outstandingSteps, roleTitle, routeOf, seatedRoles } from './rules/common.ts';
import { evaluateObjective } from './rules/objectives.ts';
import { actionStage, usesScanner, createPlanPreview, orderedPlan, plannedState, previewAction } from './rules/drafts.ts';
import { transferTarget } from './rules/transfers.ts';
import { dayConsultant, phaseDurationMs } from './rules/phases.ts';
import type { ActionOutcome, BedKind, Booking, Box, Colour, Dashboard, DraftAction, Patient, Phase, PublicState, Risk, Role, Route, RouteCode, SessionState, TableState } from './types.ts';
import { CONFIRMED, ROLE_ORDER } from './types.ts';

// ───────────────────────────── view types ─────────────────────────────

export interface Affordance {
  id: string;
  cmd: ActionCommand;
  label: string;
  cost: number;
  enabled: boolean;
  reason?: string;
  bet?: string;
  /** Risk if the diagnosis is wrong, when the bet mentions one (for colouring it). */
  risk?: Risk;
  danger?: boolean;
  group: 'certainty' | 'route' | 'treat' | 'move' | 'help' | 'seat' | 'lead' | 'prompt' | 'layer';
}

export interface RouteStepView {
  label: string;
  kind: string;
  state: 'done' | 'next' | 'todo';
  warn: boolean;
  by: string;
}

export interface PatientView {
  id: string;
  name: string;
  flavour: string;
  colour: Colour;
  provisional: string;
  believed: string;
  flipped: boolean;
  box: Box;
  printedBox: Box;
  ruledOut: string[];
  differentials: string[];
  ticks: number;
  clockLen: number;
  needsStabilise: boolean;
  where: string;
  holder: Role | null;
  route: { code: RouteCode; label: string; steps: RouteStepView[]; outstanding: number } | null;
  planned: { route: PatientView['route']; needsStabilise: boolean };
  revealed: { line: string; text: string; turn: number }[];
  flags: { bouncedBack: boolean; divertedFrom: number | null; referredBy: Role | null; special: string | null; cluster: boolean; unstaffed: boolean; pinned: boolean; ventHeld: boolean; decree: boolean; arrivedThisTurn: boolean; waitingICU: boolean; supported: boolean; overflowDelayed: boolean; dischargeNotBeforeTurn: number | null };
  page: number;
  actions: Affordance[];
}

export interface BoardView {
  lead: Role;
  theatreDemand: number;
  /** The viewer's own service only: a smoothed estimate with the chance it is too high or too low. */
  forecast: { turn: number; hour: string; role: Role; estimate: number; tooHighPct: number; tooLowPct: number }[];
  bookings: { kind: Booking['kind']; bookedTurn: number; from: number; until: number | null; used: number; request: 'PLANNED' | 'CRISIS'; status: 'upcoming' | 'current' | 'finished' }[];
  /** Numeric fields are real only for the viewer's own seat (and Control); other services show null. */
  seats: { role: Role; name: string | null; bot: boolean; beds: number | null; used: number | null; corridor: number | null; interrupts: number | null; staff: number | null }[];
  /** Emergency alone: a coarse three-level sense of each other service, never counts. */
  capacity: { role: Role; level: 'available' | 'delays' | 'extreme' }[] | null;
  scanner: { box: number; usedBy: Role | null; allocatedTo: Role | null; closed: boolean }[];
  theatre: { closed: string | null; used: boolean; booked: boolean; anaesthetic: boolean; allocatedTo: Role | null };
  secondTheatre: { closed: string | null; used: boolean } | null;
  icu: { beds: number; occupied: number; supportsNeeded: number } | null;
  divert: boolean;
  room: { table: number; divert: boolean }[];
  divertNotice: { fromTable: number; count: number }[];
  locum: { actionsLeft: number } | null;
  locumComing: boolean;
  callsLeft: number;
  timeOutUsed: boolean;
  timeOutNow: boolean;
  /** At 0700 (and after): the day consultant this table hands over to. */
  dayTeam: { name: string; line: string; mult: number } | null;
  vents: { total: number; free: number } | null;
  blood: { units: number; ordered: boolean } | null;
  dashboard: Pick<Dashboard, 'saved' | 'harmed' | 'died'>;
}

export interface ClockView {
  turn: number;
  lastTurn: number;
  phase: Phase;
  paused: boolean;
  hold: boolean;
  phaseEndsAt: number | null;
  remainingMs: number | null;
  hour: string;
  handoverTurn: boolean;
  /** Full length of the current phase, for countdown rings. */
  phaseMs: number | null;
}

export interface DraftView {
  turn: number;
  revision: number;
  ready: boolean;
  actions: { id: string; revision: number; cmd: ActionCommand; stage: 'LOGISTICS' | 'CLINICAL' | 'DEPARTURE' | 'TRANSFER'; label: string; cost: number; scanner: boolean; priority?: 1 | 2 | 3; note?: string; reason?: string }[];
  proposedCost: number;
  remaining: number;
}

export interface CoordinationView {
  allocations: { sender: Role; slot: 'scanner0' | 'scanner1' | 'theatre'; to: Role | null; currentReason: string | null }[];
  seats: { role: Role; ready: boolean; revision: number; actions: number; scans: number | null; theatre: number | null; anaesthetic: number | null; plannedFree: number | null }[];
  referrals: { sender: Role; receiver: Role; actionId: string; offerRevision: number; mode: 'BED' | 'OVERFLOW' | null; patient: { id: string; name: string; colour: Colour; believed: string; box: Box; ticks: number; route: RouteCode | null } }[];
  pendingTransfers: { patient: { id: string; name: string }; from: Role; to: Role; turn: number; arrivesTurn: number }[];
  timeOut: boolean;
}

export interface WorkloadView {
  census: number;
  beds: number;
  bedExcess: number;
  staffAssigned: number | null;
  staffDemand: number | null;
  staffShortfall: number | null;
  exposure: { ratio: number; probability: number; checked: boolean } | null;
}

export interface ReceiptView {
  id: string;
  turn: number;
  boundary: 'COMMIT' | 'RELEASE';
  events: { type: string; seat: Role | null; patient: string | null; severity: number; text: string }[];
  outcomes: ActionOutcome[];
}

export interface SeatView {
  kind: 'seat';
  session: { name: string; preset: string; presetLabel: string; tables: number; layers: SessionState['config']['layers'] };
  clock: ClockView;
  broadcast: string | null;
  events: EventCard[];
  draft: DraftView | null;
  coordination: CoordinationView;
  receipts: ReceiptView[];
  workload: WorkloadView;
  me: {
    table: number;
    role: Role;
    title: string;
    name: string | null;
    fiveSeat: boolean;
    isLead: boolean;
    actions: { available: number; spent: number };
    objective: { key: string; title: string; text: string } | null;
    huddleSentence: string;
    staff: number | null;
  };
  beds: { index: number; label: string; patient: PatientView | null; disabled: boolean }[];
  corridor: PatientView[];
  opened: PatientView[];
  prompts: { id: string; kind: string; text: string; options: { value: string; label: string }[] }[];
  interrupts: { id: string; title: string; state: string; age: number; bonus: boolean }[];
  board: BoardView;
  seatActions: Affordance[];
  teammates: { role: Role; title: string; name: string | null }[];
  debrief: null | {
    dashboard: Dashboard;
    objective: { title: string; achieved: boolean; detail: string } | null;
    traps: { name: string; provisional: string; truth: string; bias: string[] }[];
  };
}

// ───────────────────────────── helpers ─────────────────────────────

const NUM = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const num = (n: number) => NUM[n] ?? String(n);

/** Copy only accepted action fields into the public saved plan. */
function commandView(cmd: ActionCommand): ActionCommand {
  const out: Record<string, unknown> = { type: cmd.type };
  for (const key of ['p', 'code', 'theatre', 'action', 'to', 'i', 'kind', 'slot', 'on', 'choice', 'note', 'n', 'pinned']) {
    if (key in cmd) out[key] = (cmd as Record<string, unknown>)[key];
  }
  return out as ActionCommand;
}

/** Describe saved intent using public costs and prerequisites. */
function draftActionView(pub: PublicState, t: TableState, role: Role, action: DraftAction): DraftView['actions'][number] {
  const cmd = commandView(action.cmd);
  const p = 'p' in cmd && cmd.p ? pub.patients[cmd.p] : undefined;
  const choices = p && hasAccess(pub, t, role, p) ? patientActions(pub, t, role, p) : seatActions(pub, t, role);
  const choice = choices.find(a => JSON.stringify(a.cmd) === JSON.stringify(cmd));
  const reason = pub.clock.phase === 'ACT' ? checkSeatCommand(pub, t.no, role, cmd, true) : null;
  const cost = HANDLERS[cmd.type].cost(pub, t, role, cmd as never);
  const label = choice?.label ?? cmd.type.replaceAll('_', ' ').toLowerCase();
  const scanner = usesScanner(action);
  return { id: action.id, revision: action.revision, cmd, stage: actionStage(cmd), scanner, label: `${label}${p ? ` · ${p.name}` : ''}`, cost,
    ...(scanner ? { priority: action.priority ?? 2, note: SCAN_NOTE } : {}), ...(reason ? { reason } : {}) };
}

const SCAN_NOTE = 'If scanners are busy, higher-priority scans go first. A scan that cannot run gives you a bonus action next turn.';

/** Rebuild a saved plan entry for SET_DRAFT, keeping the player's scan priority. */
export function draftEntry(a: { id: string; cmd: ActionCommand; priority?: 1 | 2 | 3 }): { id: string; cmd: ActionCommand; priority?: 1 | 2 | 3 } {
  return { id: a.id, cmd: a.cmd, ...(a.priority ? { priority: a.priority } : {}) };
}

/** Project the seat's saved plan without exposing internal intent metadata. */
export function draftView(pub: PublicState, t: TableState, role: Role): DraftView | null {
  const plan = t.drafts[role];
  if (!plan || plan.turn !== pub.clock.turn || (pub.clock.phase !== 'ACT' && pub.clock.phase !== 'LOG')) return null;
  const preview = pub.clock.phase === 'ACT' ? createPlanPreview(pub, t.no) : pub;
  const current = preview.tables.find(table => table.no === t.no)!;
  const entries = new Map<string, DraftView['actions'][number]>();
  for (const action of orderedPlan(plan.actions)) {
    entries.set(action.id, draftActionView(preview, current, role, action));
    if (pub.clock.phase === 'ACT') previewAction(preview, t.no, role, action.cmd);
  }
  const actions = plan.actions.map(action => entries.get(action.id)!);
  const proposedCost = actions.reduce((sum, a) => sum + a.cost, 0);
  const budget = t.seats[role]!.actions;
  return { turn: plan.turn, revision: plan.revision, ready: plan.ready, actions, proposedCost, remaining: budget.available + budget.spent - proposedCost };
}

/** Restrict clinical offer information to its sender and receiver. */
function referralViews(pub: PublicState, t: TableState, role: Role): CoordinationView['referrals'] {
  if (pub.clock.phase !== 'ACT') return [];
  const entries = seatedRoles(t).flatMap(sender => (t.drafts[sender]?.actions ?? []).map(action => ({ sender, action })));
  return entries.flatMap(({ sender, action }) => {
    const cmd = action.cmd;
    const receiver = transferTarget(pub, action);
    if (!receiver || !('p' in cmd) || !cmd.p || (sender !== role && receiver !== role)) return [];
    const p = pub.patients[cmd.p];
    if (!p || p.table !== t.no) return [];
    const consent = t.consents.find(c => c.sender === sender && c.receiver === receiver && c.actionId === action.id && c.offerRevision === action.revision);
    return [{ sender, receiver, actionId: action.id, offerRevision: action.revision, mode: consent?.mode ?? null,
      patient: { id: p.id, name: p.name, colour: p.colour, believed: diagName(p.believedKey), box: p.box, ticks: p.ticks, route: p.route?.code ?? null } }];
  });
}

/** Count a seat's beds free after valid saved ordinary discharges and accepted inbound offers; a count, never a plan. */
function plannedFreeBeds(pub: PublicState, t: TableState, role: Role): number {
  const observed = t.seats[role]!.beds.length - t.board.bedCounts[role].used;
  const saved = t.drafts[role]?.actions.some(a => a.cmd.type === 'DISCHARGE' || (a.cmd.type === 'USE_LOCUM' && a.cmd.action === 'DISCHARGE'));
  const departing = new Set((saved ? draftView(pub, t, role)?.actions ?? [] : []).flatMap(a => {
    const cmd = a.cmd;
    if (a.reason || !(cmd.type === 'DISCHARGE' || (cmd.type === 'USE_LOCUM' && cmd.action === 'DISCHARGE'))) return [];
    const p = pub.patients[cmd.p];
    return p?.seat === role && p.loc.kind === 'bed' && (p.flags.dischargeNotBeforeTurn ?? 0) <= pub.clock.turn ? [p.id] : [];
  }));
  const inbound = new Set(t.consents.filter(c => c.receiver === role && c.mode !== null
    && t.drafts[c.sender]?.actions.some(a => a.id === c.actionId && a.revision === c.offerRevision)).map(c => `${c.sender}:${c.actionId}`));
  return observed + departing.size - inbound.size;
}

const inTheatre = (r: Role): boolean => r === 'SURG' || r === 'ICU';

/** Summarize shared demand without publishing teammates' patient plans. */
function coordinationView(pub: PublicState, t: TableState, role: Role): CoordinationView {
  const roles = seatedRoles(t);
  const seats = roles.map(r => {
    const plan = t.drafts[r];
    const actions = plan?.actions ?? [];
    return { role: r, ready: plan?.ready ?? false, revision: plan?.revision ?? 0, actions: actions.length,
      // Scan counts are your own; Theatre counts pass only between Surgeon and Intensivist.
      scans: r === role ? actions.filter(usesScanner).length : null,
      theatre: r === role || (inTheatre(r) && inTheatre(role)) ? actions.filter(a => a.cmd.type === 'OPERATE').length : null,
      anaesthetic: r === role || (inTheatre(r) && inTheatre(role)) ? actions.filter(a => a.cmd.type === 'ANAESTHETISE').length : null,
      plannedFree: r === role ? plannedFreeBeds(pub, t, r) : null };
  });
  return { allocations: allocationProposals(pub, t), seats, referrals: referralViews(pub, t, role), pendingTransfers: pendingTransferViews(pub, t, role),
    timeOut: roles.some(r => t.drafts[r]?.actions.some(a => a.cmd.type === 'TIME_OUT')) };
}

/** Share reservation requests without predicting dynamic Lead handoffs. */
function allocationProposals(pub: PublicState, t: TableState): CoordinationView['allocations'] {
  if (pub.clock.phase !== 'ACT') return [];
  return seatedRoles(t).flatMap(sender => {
    const plan = t.drafts[sender];
    if (plan?.turn !== pub.clock.turn) return [];
    return plan.actions.flatMap(({ cmd }) => cmd.type === 'ALLOCATE' ? [{
      sender, slot: cmd.slot, to: cmd.to, currentReason: checkSeatCommand(pub, t.no, sender, cmd),
    }] : []);
  });
}

/** Keep boundary audiences independent of current patient ownership. */
function receiptViews(t: TableState, role: Role): ReceiptView[] {
  return t.receipts.map(receipt => ({
    id: receipt.id, turn: receipt.turn, boundary: receipt.boundary,
    events: receipt.events.filter(e => (e.table === null || e.table === t.no) && (e.audience.includes(`seat:${role}`) || (e.audience.includes('table') && !e.patient)))
      .map(e => ({ type: e.type, seat: e.seat ?? null, patient: e.patient ?? null, severity: e.severity, text: e.text })),
    outcomes: receipt.outcomes.filter(o => o.seat === role).map(o => ({ seat: o.seat, actionId: o.actionId, status: o.status, reason: o.reason })),
  }));
}

/** Show committed destinations without releasing new clinical information. */
function pendingTransferViews(pub: PublicState, t: TableState, role: Role): CoordinationView['pendingTransfers'] {
  return t.pendingTransfers.filter(move => move.from === role || move.to === role).map(move => ({
    patient: { id: move.patient, name: pub.patients[move.patient]!.name },
    from: move.from, to: move.to, turn: move.turn, arrivesTurn: move.turn + 1,
  }));
}

/** Distinguish live capacity from the exposure fixed when ACTION opened. */
export function workloadView(pub: PublicState, t: TableState, role: Role): WorkloadView {
  const st = t.seats[role]!;
  const patients = listOf(pub, t, role);
  const staffAssigned = pub.config.layers.staff ? st.staff : null;
  const staffDemand = pub.config.layers.staff ? patients.reduce((sum, p) => sum + (role === 'ICU' || p.colour === 'RED' ? 2 : 1), 0) : null;
  const exposure = t.overflow[role];
  return { census: patients.length, beds: st.beds.length, bedExcess: Math.max(0, patients.length - st.beds.length),
    staffAssigned, staffDemand, staffShortfall: staffAssigned === null ? null : Math.max(0, staffDemand! - staffAssigned),
    exposure: exposure ? { ratio: exposure.ratio, probability: exposure.probability, checked: exposure.checked } : null };
}

/** Release personal learning details only after the shift ends. */
function seatDebrief(s: SessionState, t: TableState, role: Role): SeatView['debrief'] {
  if (s.clock.phase !== 'DEBRIEF' && s.clock.phase !== 'ENDED') return null;
  const st = t.seats[role]!;
  const obj = st.objective ? objectiveCard(st.objective) : null;
  const res = evaluateObjective(s, t, st);
  return {
    dashboard: { ...t.dashboard },
    objective: obj && res ? { title: obj.title, achieved: res.achieved, detail: res.detail } : null,
    traps: t.trapList.map(id => {
      const p = s.patients[id]!;
      const back = s.backs[id]!;
      return { name: p.name, provisional: diagName(p.provisionalKey), truth: diagName(back.truthKey), bias: back.bias.slice() };
    }),
  };
}

export function clockView(s: PublicState): ClockView {
  const row = s.script[s.clock.turn - 1];
  const handover = !!row?.handover && (s.clock.phase === 'HANDOVER' || s.clock.phase === 'DEBRIEF' || s.clock.phase === 'ENDED');
  return {
    turn: s.clock.turn,
    lastTurn: s.clock.lastTurn,
    phase: s.clock.phase,
    paused: s.clock.paused,
    hold: s.clock.hold,
    phaseEndsAt: s.clock.phaseEndsAt,
    remainingMs: s.clock.remainingMs,
    hour: s.clock.turn === 0 ? '2100' : shiftHour(row?.scriptTurn ?? s.clock.turn, handover),
    handoverTurn: !!row?.handover,
    phaseMs: phaseDurationMs(s as SessionState),
  };
}

function bedLabel(role: Role, index: number): string {
  const kind = BED_OF[role];
  return kind === 'bay' ? `Bay ${index + 1}` : `${kind} ${index + 1}`;
}

function whereOf(p: Patient): string {
  if (!p.seat) return '';
  if (p.loc.kind === 'corridor') return 'Corridor';
  if (p.loc.kind === 'bed') return bedLabel(p.seat, p.loc.index);
  return '';
}

const riskWords = (r: string) => RISK_IF_WRONG[r as Risk] ?? r;

function riskBet(p: Patient, risk: string, rate: string | null): string {
  const conf = p.box === CONFIRMED ? 'confirmed' : `at ${p.box}% sure`;
  return `Treating ${conf}; if wrong: ${riskWords(risk)}${rate ? `; complications ${rate.toLowerCase()}` : ''}`;
}

function routeView(p: Patient): PatientView['route'] {
  if (!p.route) return null;
  const r = p.route.code === 'X' ? EXPLORATORY_POLISHING : routeOf(p);
  if (!r) return null;
  const names: Record<string, string> = { S: 'Surgical', M: 'Medical', P: 'Psych', G: 'Standard', X: 'Exploratory Polishing' };
  const owner = (by: string[]) => by.map((b) => ({ EM: 'Em', SURG: 'Surg', PHYS: 'Phys', PSYCH: 'Psych', ICU: 'ICU', ANY: 'any' })[b] ?? b).join('/');
  return {
    code: p.route.code,
    label: `${names[p.route.code]} route`,
    steps: r.steps.map((st, i) => ({ label: st.label, kind: st.kind, state: i < p.route!.next ? 'done' : i === p.route!.next ? 'next' : 'todo', warn: !!st.warn, by: st.kind === 'THEATRE' ? 'Surg+ICU' : owner(st.by) })),
    outstanding: outstandingSteps(p),
  };
}

function aff(pub: PublicState, t: TableState, role: Role, cmd: ActionCommand, label: string, group: Affordance['group'], extra: Partial<Affordance> = {}): Affordance {
  const reason = checkSeatCommand(pub, t.no, role, cmd);
  const h = HANDLERS[cmd.type];
  const cost = h.cost(pub, t, role, cmd as never);
  const key = [cmd.type, 'p' in cmd ? cmd.p : '', 'to' in cmd ? String(cmd.to) : '', 'code' in cmd ? cmd.code : '', 'action' in cmd ? cmd.action : '', 'slot' in cmd ? cmd.slot : '', 'kind' in cmd ? cmd.kind : '', 'theatre' in cmd && cmd.theatre === 1 ? 'theatre1' : ''].filter(Boolean).join('-');
  return { id: key, cmd, label, cost, enabled: reason === null, reason: reason ?? undefined, group, ...extra };
}

/** Keep owner-only coordination and assistance separate from clinical choices. */
function ownPatientActions(pub: PublicState, t: TableState, role: Role, p: Patient): Affordance[] {
  if (p.seat !== role) return [];
  const out: Affordance[] = [];
  const id = p.id;
  for (const r of seatedRoles(t)) {
    if (r === role) continue;
    const a = aff(pub, t, role, { type: 'REFER', p: id, to: r }, r === 'ICU' ? 'Step up to ICU (their team must agree)' : `Transfer to ${roleTitle(r)} (their team must agree)`, 'move');
    if (a.enabled || r === 'ICU') out.push(a);
  }
  for (const r of seatedRoles(t)) {
    if (r === role) continue;
    const open = t.openCards.some((o) => o.patient === id && o.to === r);
    const a = aff(pub, t, role, { type: open ? 'CLOSE_CARD' : 'OPEN_CARD', p: id, to: r }, open ? `Close to ${roleTitle(r)}` : `Ask ${roleTitle(r)} for an opinion (the patient stays with you)`, 'help');
    if (open || a.enabled) out.push(a);
  }
  for (const action of locumPresent(pub, t) ? ['ASSESS', 'STABILISE', 'TREAT', 'DISCHARGE'] as const : []) {
    const a = aff(pub, t, role, { type: 'USE_LOCUM', p: id, action }, `Locum: ${action.toLowerCase()}`, 'help');
    if (a.enabled) out.push(a);
  }
  if (pub.config.layers.staff) out.push(aff(pub, t, role, { type: 'PIN_STAFF', p: id, pinned: !p.flags.pinnedStaff }, p.flags.pinnedStaff ? 'Let staff move again' : 'Keep staff with this patient', 'layer'));
  return out;
}

/** Action affordances for one patient visible to a seat. */
function patientActions(pub: PublicState, t: TableState, role: Role, p: Patient): Affordance[] {
  const out: Affordance[] = [];
  const id = p.id;
  out.push(aff(pub, t, role, { type: 'ASSESS', p: id }, 'Assess at the bedside', 'certainty'));
  for (const k of namedOpinions(t, p)) {
    const who = k === 'OPINION_PHYS' ? 'PHYS' : k === 'OPINION_PSYCH' ? 'PSYCH' : 'SURG';
    if (who === role || (who === 'PHYS' && t.effects.cover === role && p.seat === 'PHYS')) out.push(aff(pub, t, role, { type: 'OPINION', p: id }, `Give a ${roleTitle(who).toLowerCase()} opinion`, 'certainty'));
  }
  out.push(aff(pub, t, role, { type: 'SCAN', p: id }, 'Scan for certainty', 'certainty'));
  if (freeAssessSource(pub, t, role)) out.push(aff(pub, t, role, { type: 'FREE_ASSESS', p: id }, `Free Assess (${freeAssessSource(pub, t, role)})`, 'certainty'));
  if (p.box === CONFIRMED) {
    const so = aff(pub, t, role, { type: 'SECOND_OPINION', p: id }, 'Second opinion (free)', 'certainty');
    if (so.enabled) out.push(so);
  }
  if (p.needsStabilise) out.push(aff(pub, t, role, { type: 'STABILISE', p: id }, p.believedKey === 'combustion' ? 'Stabilise: Damp' : 'Stabilise', 'treat'));
  // routes
  for (const r of availableRoutes(pub, t, p)) {
    if (p.route && p.route.code === r.code && p.route.diag === p.believedKey) continue;
    const steps = r.steps.map((x) => x.label).join(' → ');
    const a = aff(pub, t, role, { type: 'START_ROUTE', p: id, code: r.code }, `Start ${r.code === 'X' ? 'Exploratory Polishing' : `${r.code} route`}: ${steps}`, 'route', {
      risk: r.riskIfWrong,
      bet: `${r.turns} turn${r.turns > 1 ? 's' : ''}; ${r.uses.length ? `uses ${r.uses.join(', ')}` : 'no shared kit'}; if wrong: ${riskWords(r.riskIfWrong)}${r.complRate ? `; complications ${r.complRate.toLowerCase()}` : ''}`,
    });
    if (a.enabled) out.push(a);
  }
  const st = p.route?.code === 'X' ? EXPLORATORY_POLISHING.steps[0]! : nextStep(p);
  const route = p.route?.code === 'X' ? EXPLORATORY_POLISHING : routeOf(p);
  if (role === 'SURG' && t.board.theatre.booking?.patient === id && st?.kind !== 'THEATRE')
    out.push(aff(pub, t, role, { type: 'OPERATE', p: id, theatre: 1 }, 'Second Theatre (anaesthetist included)', 'treat'));
  if (st && route) {
    const unconfirmed = p.box !== CONFIRMED;
    const bet = unconfirmed ? riskBet(p, route.riskIfWrong, st.warn ? route.complRate : null) : st.warn && route.complRate ? `Complications ${route.complRate.toLowerCase()}` : undefined;
    const danger = unconfirmed && route.riskIfWrong === 'CRITICAL';
    const risk = unconfirmed ? route.riskIfWrong : undefined;
    if (st.kind === 'THEATRE') {
      if (role === 'EM') out.push(aff(pub, t, role, { type: 'RED_BLANKET', p: id }, 'Red blanket: send this patient straight into a fully staffed Theatre (once every 3 turns)', 'treat', { bet, danger, risk }));
      if (role === 'SURG') out.push(
        aff(pub, t, role, { type: 'OPERATE', p: id }, `Operate: ${st.label.replace('Theatre: ', '')}`, 'treat', { bet, danger, risk }),
        aff(pub, t, role, { type: 'OPERATE', p: id, theatre: 1 }, `Second Theatre: ${st.label.replace('Theatre: ', '')} (anaesthetist included)`, 'treat', { bet, danger, risk }),
      );
    } else if (st.kind === 'TREAT' || st.kind === 'REASSURE' || st.kind === 'SCAN') {
      out.push(aff(pub, t, role, { type: 'TREAT', p: id }, st.label, 'treat', { bet, danger, risk }));
    } else if (st.kind === 'STEP_DOWN' && role === 'ICU') {
      out.push(aff(pub, t, role, { type: 'STEP_DOWN', p: id }, `Step down to ${st.bed}`, 'move'));
    } else if (st.kind === 'DISCHARGE') {
      out.push(aff(pub, t, role, { type: 'DISCHARGE', p: id }, st.label, 'treat', { bet: unconfirmed ? riskBet(p, route.riskIfWrong, null) : undefined, risk }));
    }
    if (st.kind === 'OBSERVE') out.push(aff(pub, t, role, { type: 'EARLY_DISCHARGE', p: id }, 'Send home early (skip observation)', 'treat'));
  }
  if (role === 'ICU' && p.seat === 'ICU' && p.loc.kind === 'bed') out.push(aff(pub, t, role, { type: 'SUPPORT', p: id }, 'Support', 'treat'));
  out.push(...ownPatientActions(pub, t, role, p));
  // keep disabled affordances only for the core actions, so the dock explains itself
  const core = new Set(['ASSESS', 'SCAN', 'TREAT', 'OPERATE', 'DISCHARGE', 'STABILISE', 'SUPPORT', 'STEP_DOWN', 'REFER', 'RED_BLANKET']);
  return out.filter((a) => a.enabled || core.has(a.cmd.type));
}

export function patientView(pub: PublicState, t: TableState, p: Patient, viewer: Role | null): PatientView {
  const d = diag(p.believedKey);
  const prov = diag(p.provisionalKey);
  const supported = p.seat === 'ICU' && (p.turnScratch.supports ?? 0) >= supportsNeeded(pub);
  const st = nextStep(p);
  return {
    id: p.id,
    name: p.name,
    flavour: p.flavour,
    colour: p.colour,
    provisional: prov.frontName ?? prov.name,
    believed: diagName(p.believedKey),
    flipped: p.believedKey !== p.provisionalKey,
    box: p.box,
    printedBox: p.flags.cluster ? 90 : p.printedBox,
    ruledOut: p.ruledOut.map(diagName),
    differentials: d.differentials.map(diagName),
    ticks: p.ticks,
    clockLen: clockLength(p.colour),
    needsStabilise: p.needsStabilise,
    where: whereOf(p),
    holder: p.seat,
    route: routeView(p),
    planned: { route: routeView(p), needsStabilise: p.needsStabilise },
    revealed: p.revealed.map((r) => ({ line: r.line, text: r.text, turn: r.turn })),
    flags: {
      bouncedBack: !!p.flags.bouncedBack,
      divertedFrom: p.flags.divertedFrom ?? null,
      referredBy: p.referral && p.referral.turn + 1 === pub.clock.turn ? p.referral.from : null,
      special: p.flags.special ?? null,
      cluster: !!p.flags.cluster,
      unstaffed: !!p.flags.unstaffed,
      pinned: !!p.flags.pinnedStaff,
      ventHeld: !!p.flags.ventHeld,
      decree: !!p.flags.decree,
      arrivedThisTurn: p.arrivedTurn === pub.clock.turn,
      waitingICU: !!st && st.kind === 'ICU' && p.seat !== 'ICU',
      supported,
      overflowDelayed: !!p.flags.overflowDelayed,
      dischargeNotBeforeTurn: p.flags.dischargeNotBeforeTurn ?? null,
    },
    page: d.page,
    actions: viewer ? patientActions(pub, t, viewer, p) : [],
  };
}

export function huddleSentence(pub: PublicState, t: TableState, role: Role): string {
  const list = listOf(pub, t, role);
  const title = roleTitle(role);
  if (list.length === 0) return `${title}: nobody on my list. I have ${num(t.seats[role]!.beds.length)} free beds.`;
  const by = (c: Colour) => list.filter((p) => p.colour === c).length;
  const parts = (['RED', 'AMBER', 'GREEN'] as Colour[]).filter((c) => by(c) > 0).map((c) => `${num(by(c))} ${c.toLowerCase()}`);
  const reds = list.filter((p) => p.colour === 'RED');
  const redBits = reds.map((p) => `${reds.length > 1 ? `${p.name.split(' ')[0]}, ` : 'the red is '}at ${p.ticks === 0 ? 'zero' : num(p.ticks)} of four, ${p.box === CONFIRMED ? 'confirmed' : `${p.box}%`}${p.needsStabilise ? ', not yet stabilised' : ''}`);
  const corridor = list.filter((p) => p.loc.kind === 'corridor').length;
  return `${title}: ${num(list.length)} patient${list.length > 1 ? 's' : ''}, ${parts.join(', ')}${redBits.length ? `; ${redBits.join('; ')}` : ''}${corridor ? `; ${num(corridor)} in the corridor` : ''}.`;
}

const OBSERVE_OWNER: Partial<Record<BedKind | 'any', Role>> = { SW: 'SURG', MW: 'PHYS', QW: 'PSYCH' };

/** Where a route first leaves Emergency: its first step only another service can do. */
function firstDestination(route: Route): Role | null {
  for (const step of route.steps) {
    const to = step.kind === 'OBSERVE' ? step.bed && OBSERVE_OWNER[step.bed] : step.by.includes('EM') || step.by.includes('ANY') ? undefined : step.by[0];
    if (to) return to as Role;
  }
  return null;
}

/** Share of Emergency patients a service receives, from the Compendium's route destinations. */
export function referralShare(role: Role): number {
  const routes = COMPENDIUM.flatMap((d) => d.routes);
  return routes.filter((r) => firstDestination(r) === role).length / routes.length;
}

const round5 = (pct: number): number => Math.round(pct / 5) * 5;

/**
 * A smoothed estimate of the viewer's own service, never the exact schedule.
 * Ceiling: overlapping windows and 5% bands can be differenced only to window-mean precision.
 */
function forecast(pub: PublicState, role: Role): BoardView['forecast'] {
  if (pub.clock.phase !== 'ACT') return [];
  const turns = pub.config.preset === 'EXIT' ? 1 : 2;
  const share = role === 'EM' ? 1 : referralShare(role);
  return pub.script.slice(pub.clock.turn, pub.clock.turn + turns).map((row, i) => {
    const at = pub.clock.turn + i;
    const window = pub.script.slice(Math.max(0, at - 1), at + 2).map((r) => Math.round(r.arrivals * share));
    const estimate = Math.round(window.reduce((a, b) => a + b, 0) / window.length);
    const pct = (hit: (v: number) => boolean) => round5(100 * window.filter(hit).length / window.length);
    return { turn: row.gameTurn, hour: shiftHour(row.scriptTurn, row.handover), role, estimate, tooHighPct: pct((v) => v < estimate), tooLowPct: pct((v) => v > estimate) };
  });
}

/** Coarse level from the last Review's counts; the thresholds are the only thing Emergency learns. */
function capacityLevel(bc: TableState['board']['bedCounts'][Role]): 'available' | 'delays' | 'extreme' {
  if (bc.corridor >= 2) return 'extreme';
  return bc.corridor === 1 || bc.used >= bc.cap ? 'delays' : 'available';
}

/** Keeps resource reservations public without exposing other seats' patients. */
function boardView(pub: PublicState, t: TableState, viewer?: Role): BoardView {
  const turn = pub.clock.turn;
  const loc = locumPresent(pub, t);
  const closedScan = pub.room.activeEvents.some((e) => (e.key === 'E4' || e.key === 'E9' || e.key === 'E42') && e.turn <= turn && turn <= e.until);
  const scanCap = pub.room.activeEvents.some((e) => e.key === 'E1' && e.turn <= turn && turn <= e.until) ? 1 : 2;
  const thClosed = pub.room.activeEvents.find((e) => ['E3', 'E9', 'E26'].includes(e.key) && e.turn <= turn && turn <= e.until);
  return {
    lead: t.lead,
    theatreDemand: theatreDemand(pub, t),
    forecast: forecast(pub, viewer ?? 'EM'),
    bookings: t.bookings.map((b) => ({
      kind: b.kind, bookedTurn: b.bookedTurn, from: b.from, until: b.until, used: b.used, request: b.request,
      status: ['HANDOVER', 'DEBRIEF', 'ENDED'].includes(pub.clock.phase) || (b.until !== null && (turn > b.until || (turn === b.until && pub.clock.phase === 'LOG')))
        ? 'finished' : turn < b.from ? 'upcoming' : 'current',
    })),
    secondTheatre: t.board.secondTheatre ? { closed: thClosed ? eventCard(thClosed.key).title : null, used: t.board.secondTheatre.used } : null,
    seats: seatedRoles(t).map((r) => {
      const st = t.seats[r]!;
      const bc = t.board.bedCounts[r];
      if (viewer && viewer !== r) return { role: r, name: st.name, bot: st.bot, beds: null, used: null, corridor: null, interrupts: null, staff: null };
      return {
        role: r, name: st.name, bot: st.bot, beds: st.beds.length, used: bc.used, corridor: bc.corridor,
        interrupts: t.interrupts.filter((i) => i.seat === r && i.state !== 'RESOLVED').length, staff: pub.config.layers.staff ? st.staff : null,
      };
    }),
    capacity: viewer === 'EM' ? seatedRoles(t).filter((r) => r !== 'EM').map((r) => ({ role: r, level: capacityLevel(t.board.bedCounts[r]) })) : null,
    scanner: [0, 1].map((i) => ({ box: i, usedBy: t.board.scannerUsedBy[i] ?? null, allocatedTo: t.board.allocations.scanner[i] ?? null, closed: closedScan || i >= scanCap })),
    theatre: {
      closed: thClosed ? eventCard(thClosed.key).title : null,
      // Booking, anaesthetic and reservation detail is for the Theatre pair (and faculty).
      used: t.board.theatre.used, allocatedTo: !viewer || inTheatre(viewer) ? t.board.allocations.theatre : null,
      booked: (!viewer || inTheatre(viewer)) && !!t.board.theatre.booking, anaesthetic: (!viewer || inTheatre(viewer)) && !!t.board.theatre.anaesthetic,
    },
    icu: !viewer || viewer === 'ICU' ? { beds: t.seats.ICU?.beds.length ?? 0, occupied: t.board.bedCounts.ICU.used, supportsNeeded: supportsNeeded(pub) } : null,
    divert: t.board.divert,
    room: pub.tables.map((x) => ({ table: x.no, divert: x.board.divert })),
    divertNotice: t.divertNotice.filter((d) => d.turn === turn).map((d) => ({ fromTable: d.fromTable, count: d.count })),
    locum: loc ? { actionsLeft: loc.actionsLeft } : null,
    locumComing: t.locums.some((l) => l.arrivesTurn > turn),
    callsLeft: PRESETS[pub.config.preset].callsForHelp - t.callsForHelp,
    timeOutUsed: t.timeOut.used,
    timeOutNow: t.timeOut.turn === turn,
    dayTeam: ['HANDOVER', 'DEBRIEF', 'ENDED'].includes(pub.clock.phase) ? { ...dayConsultant(pub, t.no) } : null,
    vents: t.board.vents ? { total: t.board.vents.total, free: Math.max(0, ventsFree(pub, t)) } : null,
    blood: t.board.blood ? { units: t.board.blood.units, ordered: t.board.blood.orders.length > 0 } : null,
    dashboard: { ...t.published },
  };
}

/** Counts only the table's chosen public routes with Theatre still ahead. */
function theatreDemand(pub: PublicState, t: TableState): number {
  if (pub.clock.phase !== 'ACT') return 0;
  return Object.values(pub.patients).filter((p) => {
    if (p.table !== t.no || p.status !== 'ACTIVE' || !p.route) return false;
    const route = p.route.code === 'X' ? EXPLORATORY_POLISHING : routeOf(p);
    return route?.steps.slice(p.route.next).some((step) => step.kind === 'THEATRE');
  }).length;
}

function seatActions(pub: PublicState, t: TableState, role: Role): Affordance[] {
  const out: Affordance[] = [];
  const isLead = t.lead === role;
  if (role === 'ICU') out.push(aff(pub, t, role, { type: 'ANAESTHETISE' }, t.board.theatre.booking ? 'Anaesthetise (Theatre booked)' : 'Anaesthetise (stand by in Theatre)', 'treat'));
  if (isLead) {
    if (pub.clock.phase === 'ACT') {
      out.push(aff(pub, t, role, { type: 'BOOK', kind: 'LOCUM' }, `Book Locum: turn ${pub.clock.turn + 1} onward`, 'lead'));
      out.push(aff(pub, t, role, { type: 'BOOK', kind: 'THEATRE' }, `Book second Theatre: turns ${pub.clock.turn + 2}–${pub.clock.turn + 3}, anaesthetist included`, 'lead'));
      out.push(aff(pub, t, role, { type: 'BOOK', kind: 'BED_MEETING' }, `Book bed meeting: turns ${pub.clock.turn + 2}–${pub.clock.turn + 3}, first ward discharge free`, 'lead'));
    }
    out.push(aff(pub, t, role, { type: 'CALL_FOR_HELP' }, `Call for help: Locum in turn ${pub.clock.turn + 1}`, 'lead'));
    out.push(aff(pub, t, role, { type: 'TIME_OUT' }, 'Time-Out: pause the whole table (once)', 'lead'));
    for (const slot of ['scanner0', 'scanner1', 'theatre'] as const) {
      for (const r of [...seatedRoles(t), null]) {
        const label = `${slot === 'theatre' ? 'Theatre' : slot === 'scanner0' ? 'Scanner 1' : 'Scanner 2'} → ${r ? roleTitle(r) : slot === 'theatre' ? 'shared in table order' : 'open to everyone, highest priority first'}`;
        const a = aff(pub, t, role, { type: 'ALLOCATE', slot, to: r }, label, 'lead');
        if (a.enabled) out.push(a);
      }
    }
    for (const on of [true, false]) {
      const a = aff(pub, t, role, { type: 'SET_DIVERT', on }, on ? 'Go on divert' : 'Come off divert', 'lead');
      if (a.enabled) out.push(a);
    }
  }
  if (pub.config.layers.blood) out.push(aff(pub, t, role, { type: 'ORDER_BLOOD' }, 'Order four units of blood', 'layer'));
  if (pub.config.layers.staff && pub.clock.phase === 'ACT') {
    for (const r of seatedRoles(t)) if (r !== role) {
      const a = aff(pub, t, role, { type: 'LEND_STAFF', to: r, n: 1 }, `Lend 1 Staff to ${roleTitle(r)}`, 'layer');
      if (a.enabled) out.push(a);
    }
  }
  for (const i of t.interrupts.filter((x) => x.seat === role && x.state !== 'RESOLVED')) {
    if (i.bonus) {
      for (const p of listOf(pub, t, role)) out.push(aff(pub, t, role, { type: 'RESOLVE_INTERRUPT', i: i.id, p: p.id }, `Take the critical result: ${p.name}`, 'seat'));
    } else out.push(aff(pub, t, role, { type: 'RESOLVE_INTERRUPT', i: i.id }, `Deal with: ${i.title}`, 'seat'));
  }
  for (const q of t.prompts.filter((x) => x.seat === role)) {
    if (q.kind === 'E17') for (const c of ['COMPLY', 'CHALLENGE'] as const) out.push(aff(pub, t, role, { type: 'EVENT_CHOICE', choice: c }, c === 'COMPLY' ? 'Comply with the decree' : 'Challenge the decree (−1 action)', 'prompt'));
    if (q.kind === 'E23') for (const r of q.options ?? []) out.push(aff(pub, t, role, { type: 'PASS_LEAD', to: r as Role }, `Hand the Lead to ${roleTitle(r as Role)}`, 'prompt'));
    if (q.kind === 'E24') for (const r of q.options ?? []) out.push(aff(pub, t, role, { type: 'ASSIGN_COVER', to: r as Role }, `${roleTitle(r as Role)} covers my list`, 'prompt'));
  }
  return out.filter((a) => a.enabled || a.group === 'lead' || a.cmd.type === 'ANAESTHETISE');
}

function promptViews(pub: PublicState, t: TableState, role: Role): SeatView['prompts'] {
  return t.prompts.filter((q) => q.seat === role).map((q) => {
    const opts = (q.options ?? []).map((v) => ({ value: v, label: pub.patients[v]?.name ?? (ROLE_ORDER.includes(v as Role) ? roleTitle(v as Role) : v) }));
    const text = q.kind === 'E17' ? 'The Consultant has decreed Exploratory Polishing for every Glowing patient. Comply or challenge?'
      : q.kind === 'E23' ? 'You are called to the boss. Hand over your priorities aloud, then pass the Lead.'
      : q.kind === 'E24' ? 'MET call in the Annexe. Who covers your list for two turns?'
      : 'Choose';
    return { id: q.id, kind: q.kind, text, options: opts };
  });
}

// ───────────────────────────── seat projection ─────────────────────────────

export function projectSeat(s: SessionState, tableNo: number, role: Role): SeatView {
  const pub: PublicState = s;
  const t = pub.tables.find((x) => x.no === tableNo)!;
  const st = t.seats[role]!;
  const turnEvents = pub.room.thisTurnEvents.map((k) => eventCard(k));
  const hasPlan = t.drafts[role]?.actions.length || t.drafts.PHYS?.actions.some(a => a.cmd.type === 'ASSIGN_COVER');
  const planned = pub.clock.phase === 'ACT' && hasPlan ? plannedState(pub, t.no, role) : pub;
  const plannedTable = planned.tables.find(table => table.no === t.no)!;
  const pv = (p: Patient): PatientView => ({ ...patientView(pub, t, p, null),
    planned: { route: routeView(planned.patients[p.id]!), needsStabilise: planned.patients[p.id]!.needsStabilise },
    actions: patientActions(planned, plannedTable, role, planned.patients[p.id]!).map(a =>
      p.seat === 'PHYS' && t.effects.cover !== role && plannedTable.effects.cover === role
        ? { ...a, bet: [a.bet, 'Physician cover starts only if it is still planned when the turn ends.'].filter(Boolean).join(' ') } : a) });
  const beds = st.beds.map((id, i) => {
    const p = id ? pub.patients[id] : null;
    const cap = role === 'PSYCH' && pub.room.activeEvents.some((e) => e.key === 'E10' && e.turn <= pub.clock.turn && pub.clock.turn <= e.until) ? 2 : st.beds.length;
    return { index: i, label: bedLabel(role, i), patient: p && p.status === 'ACTIVE' ? pv(p) : null, disabled: i >= cap && !p };
  });
  const corridor = st.corridor.map((id) => pub.patients[id]!).filter((p) => p.status === 'ACTIVE').map(pv);
  const opened = Object.values(pub.patients)
    .filter((p) => p.status === 'ACTIVE' && p.table === t.no && p.seat !== role && hasAccess(pub, t, role, p))
    .map(pv);
  const obj = st.objective ? objectiveCard(st.objective) : null;
  return {
    kind: 'seat',
    session: { name: pub.config.name, preset: pub.config.preset, presetLabel: PRESETS[pub.config.preset].label, tables: pub.tables.length, layers: { ...pub.config.layers } },
    clock: clockView(pub),
    broadcast: pub.room.broadcast,
    events: turnEvents,
    draft: draftView(pub, t, role),
    coordination: coordinationView(pub, t, role),
    receipts: receiptViews(t, role),
    workload: workloadView(pub, t, role),
    me: {
      table: t.no,
      role,
      title: roleTitle(role),
      name: st.name,
      fiveSeat: t.fiveSeat,
      isLead: t.lead === role,
      actions: { available: st.actions.available, spent: st.actions.spent },
      objective: obj ? { key: obj.key, title: obj.title, text: obj.text } : null,
      huddleSentence: pub.clock.turn > 0 ? huddleSentence(pub, t, role) : '',
      staff: pub.config.layers.staff ? st.staff : null,
    },
    beds,
    corridor,
    opened,
    prompts: promptViews(pub, t, role),
    interrupts: t.interrupts.filter((i) => i.seat === role && i.state !== 'RESOLVED').map((i) => ({ id: i.id, title: i.title, state: i.state, age: i.age, bonus: !!i.bonus })),
    board: boardView(pub, t, role),
    seatActions: pub.clock.phase === 'SETUP' ? [] : seatActions(planned, plannedTable, role),
    teammates: seatedRoles(t).filter((r) => r !== role).map((r) => ({ role: r, title: roleTitle(r), name: t.seats[r]!.name })),
    debrief: seatDebrief(s, t, role),
  };
}

export { boardView, promptViews };

/** Status text for one Board booking; "booked this turn" only fits a booking that ends the turn it starts. */
export function bookingStatusLabel(b: BoardView['bookings'][number]): string {
  if (b.status !== 'current') return b.status;
  return b.until === b.from ? 'booked this turn' : 'in use';
}
